/* OnboardingTour — first-login spotlight walkthrough.
   Instagram/Duolingo-style: dims the app with a dark overlay, then reveals
   a rounded rectangle "cut-out" over the feature to point at. A floating
   info card sits near the target.

   Steps are declarative: each { key, selector, title, body, badge? }.
   The selector is a CSS query. We measure the target rect every step and
   clamp everything inside the viewport (works on mobile portrait).
   If a target isn't present in the DOM the step becomes a centered card.

   Life-cycle:
   - Only shows when the user has no `onboarded_at` field (server) AND
     no local flag either (defensive).
   - Once completed/skipped → POST /api/users/me/onboarded and set a
     localStorage flag so we never re-show.
*/
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";

const STORAGE_KEY_PREFIX = "orio_onboarding_done_"; // per-user key to avoid cross-account bleed

const STEPS = [
  {
    key: "welcome",
    selector: null,
    title: "Benvenuto su ORIO",
    body: "In 30 secondi ti mostro come funziona. Il piano gratuito ti fa iniziare subito; ORIO Pro sblocca tutte le funzioni operative. Puoi saltare quando vuoi.",
    badge: "Tour · 6 passi",
  },
  {
    key: "home-avatar",
    selector: "[data-testid='home-my-stories-avatar']",
    title: "Il tuo menù rapido",
    body: "Tocca qui per pubblicare una storia (2 al giorno con il piano free), modificare il profilo o aprire il cassetto documenti.",
  },
  {
    key: "map-tab",
    selector: "[data-testid='bottom-nav-mappa']",
    title: "Mappa parlante",
    body: "Parchi, veterinari, aree cani, pericoli e utenti nella tua zona. Sempre disponibile, anche col piano free.",
  },
  {
    key: "sos",
    selector: "[data-testid='bottom-nav-sos']",
    title: "SOS · Il cuore di ORIO",
    body: "Se il cane scappa, un tap invia allerta geolocalizzata fino a 80 km. Riservato agli abbonati ORIO Pro — 30 giorni gratis.",
    badge: "PRO",
  },
  {
    key: "match",
    selector: "[data-testid='bottom-nav-match']",
    title: "Match, Park Room & Storie",
    body: "Trova compagni di passeggiata compatibili, entra nelle stanze dei parchi vicini, guadagna Karma. Disponibile con ORIO Pro.",
    badge: "PRO",
  },
  {
    key: "missioni",
    selector: "[data-testid='bottom-nav-missioni']",
    title: "Missioni & Segnalazioni",
    body: "Aiuta cani in difficoltà nella tua zona o segnala un pericolo. Guadagni Karma per ogni azione — anche col piano free.",
  },
];

export default function OnboardingTour() {
  const { user, refreshUser } = useAuth();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const [tick, setTick] = useState(0);
  const shouldRun = useMemo(() => {
    if (!user) return false;
    if (user.onboarded_at) return false;
    // Wait for the WelcomeCompleteSheet (city/birth_year mini-form) to be
    // resolved before starting the tour so the two modals don't overlap.
    // The welcome sheet appears only when user is missing BOTH city AND
    // birth_year — so once either is present, or the user dismissed the
    // sheet (skip flag), the tour is free to start.
    const missingBoth = !user.city && !user.birth_year;
    if (missingBoth) {
      if (user.id) {
        try {
          if (!localStorage.getItem("orio_welcome_seen_" + user.id)) return false;
        } catch { /* ignore */ }
      }
    }
    // Per-user local flag so users on shared devices don't inherit each
    // other's tour completion state. Skipped when user is missing an id.
    if (user.id) {
      try {
        if (localStorage.getItem(STORAGE_KEY_PREFIX + user.id)) return false;
      } catch { /* localStorage disabled — proceed */ }
    }
    return true;
  // Re-evaluate when the "tick" from the polling effect above bumps so we
  // catch the moment WelcomeCompleteSheet closes (it flips a localStorage
  // flag without mutating the `user` object).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tick]);

  // Poll the "should we run?" gate once per second while the tour hasn't
  // started yet. Cheap, and lets us react to WelcomeCompleteSheet closing
  // (which flips a localStorage flag without changing the `user` object).
  useEffect(() => {
    if (!user || user.onboarded_at || visible) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [user, visible]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  void tick;

  // Delay start so the Home layout is painted (avatar / nav mounted).
  useEffect(() => {
    if (!shouldRun) return;
    const t = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(t);
  }, [shouldRun]);

  const step = STEPS[index];

  const measure = useCallback(() => {
    if (!step || !step.selector) return setRect(null);
    const el = document.querySelector(step.selector);
    if (!el) return setRect(null);
    const r = el.getBoundingClientRect();
    // Some targets flicker to zero-size during animations — ignore those.
    if (r.width < 4 || r.height < 4) return setRect(null);
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useLayoutEffect(() => {
    if (!visible) return;
    measure();
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    const t = setInterval(measure, 500);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("scroll", on, true);
      clearInterval(t);
    };
  }, [visible, measure, index]);

  const complete = useCallback(async () => {
    setVisible(false);
    if (user?.id) {
      try { localStorage.setItem(STORAGE_KEY_PREFIX + user.id, "1"); } catch {}
    }
    try {
      await api.post("/users/me/onboarded");
      refreshUser?.();
    } catch { /* silent — will retry next login */ }
  }, [refreshUser, user]);

  const nextStep = useCallback(() => {
    hapticTap();
    if (index >= STEPS.length - 1) return complete();
    setIndex((i) => i + 1);
  }, [index, complete]);

  const skip = useCallback(() => { hapticTap(); complete(); }, [complete]);

  if (!shouldRun || !visible) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="tour-root"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[3000]"
        data-testid="onboarding-tour"
      >
        <Backdrop rect={rect} />
        <StepCard
          step={step} rect={rect} index={index} total={STEPS.length}
          onNext={nextStep} onSkip={skip}
        />
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

/* ── Backdrop with a rounded cut-out around the target rect ─────────── */
function Backdrop({ rect }) {
  const pad = 10;
  const radius = 24;
  if (!rect) {
    return (
      <div className="absolute inset-0" style={{ background: "rgba(6,6,10,0.72)", backdropFilter: "blur(3px)" }} />
    );
  }
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;
  const top = rect.top - pad;
  const left = rect.left - pad;
  return (
    <>
      <div className="absolute inset-0" style={{ background: "rgba(6,6,10,0.72)", backdropFilter: "blur(3px)" }} />
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", damping: 20, stiffness: 220 }}
        style={{
          position: "absolute", top, left, width: w, height: h,
          borderRadius: radius,
          boxShadow: "0 0 0 9999px rgba(6,6,10,0.72), 0 0 0 3px rgba(255,255,255,0.95), 0 0 40px 8px rgba(255,122,26,0.55)",
          pointerEvents: "none",
        }}
      />
      <motion.div
        initial={{ opacity: 0.9, scale: 1 }}
        animate={{ opacity: [0.9, 0.35, 0.9], scale: [1, 1.08, 1] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute", top: top - 8, left: left - 8,
          width: w + 16, height: h + 16, borderRadius: radius + 8,
          border: "2px solid rgba(255,122,26,0.55)", pointerEvents: "none",
        }}
      />
    </>
  );
}

/* ── Floating info card positioned near the target ──────────────────── */
function StepCard({ step, rect, index, total, onNext, onSkip }) {
  const vw = typeof window !== "undefined" ? window.innerWidth : 390;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  const cardStyle = useMemo(() => {
    const cardW = Math.min(360, vw - 32);
    const cardMinH = 200;
    if (!rect) {
      return { top: (vh - cardMinH) / 2, left: (vw - cardW) / 2, width: cardW };
    }
    const spaceBelow = vh - (rect.top + rect.height);
    const preferBelow = spaceBelow > cardMinH + 40;
    let top = preferBelow ? rect.top + rect.height + 20 : Math.max(24, rect.top - cardMinH - 20);
    let left = rect.left + rect.width / 2 - cardW / 2;
    left = Math.max(16, Math.min(vw - cardW - 16, left));
    return { top, left, width: cardW };
  }, [rect, vw, vh]);

  return (
    <motion.div
      key={step.key}
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ type: "spring", damping: 24, stiffness: 260 }}
      className="absolute rounded-3xl border border-white/10"
      style={{
        ...cardStyle,
        background: "linear-gradient(180deg, #FFFFFF 0%, #FDF7EE 100%)",
        boxShadow: "0 30px 70px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
      }}
      data-testid="onboarding-step-card"
    >
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 text-white"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                     boxShadow: "0 10px 22px -8px rgba(255,122,26,0.45)" }}>
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.20em] font-bold text-[#C25612]">
                {index + 1}/{total}
              </span>
              {step.badge && (
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full text-white"
                  style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
                  {step.badge}
                </span>
              )}
            </div>
            <div className="font-display text-[19px] leading-tight text-[#1A1A1A] mt-1">
              {step.title}
            </div>
          </div>
          <button onClick={onSkip} aria-label="Chiudi tour"
            data-testid="onboarding-close"
            className="h-8 w-8 rounded-full bg-[#F4EFE6] flex items-center justify-center text-[#6B6B6B]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="text-[13.5px] text-[#3A3A3A] mt-3 leading-relaxed">
          {step.body}
        </div>
        <div className="flex items-center justify-between mt-5">
          <div className="flex gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
              <span key={i} className="h-1.5 rounded-full transition-all"
                style={{ width: i === index ? 18 : 6, background: i === index ? "#FF7A1A" : "#E5DECF" }} />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={onSkip} data-testid="onboarding-skip"
              className="text-[13px] text-[#7A7A7A] px-3 py-2 rounded-full hover:bg-[#F4EFE6]">
              Salta
            </button>
            <button
              onClick={onNext}
              data-testid="onboarding-next"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white px-4 py-2 rounded-full"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                boxShadow: "0 10px 22px -8px rgba(255,122,26,0.45)",
              }}
            >
              {index >= total - 1 ? "Fatto" : "Avanti"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
